/**
 * QuizCountdownTimer Component
 * 
 * A countdown timer for virtual quizzes that counts backward from a specified duration.
 * Features:
 * - Timestamp-based timing to prevent browser throttling on inactive tabs
 * - Auto-submit when timer reaches 00:00:00
 * - Visual warning (red color) when less than 5 minutes remaining
 * - Persistent timer state using localStorage
 * 
 * @component
 */

import classes from "./QuizTimer.module.scss";
import timerIcon from "../../../assets/images/QuizImageAndIcons/timer_icon.svg";
import React from "react";
import { connect } from "react-redux";
import {
    endQuizCounter,
    startQuizCounter,
} from "../../../store/actions/actionCreators/quizActionCreator";

// Counter variables to track start/stop calls
let startcount = 0;
let endcount = 0;

class QuizCountdownTimer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            running: false,
            currentTimeHr: 0,
            currentTimeMs: 0,
            currentTimeSec: 0,
            currentTimeMin: 0,
        };
        this.watch = null;
        this.targetEndTime = null;
    }

    componentDidMount() {
        endcount = 0;
        startcount = 0;
    }

    /**
     * Format time value to always have 2 digits (e.g., 5 -> "05")
     * @param {number} val - The time value to format
     * @returns {string} Formatted time string
     */
    formatTime = (val) => {
        let value = val.toString();
        if (value.length < 2) {
            value = "0" + value;
        }
        return value;
    };

    /**
     * Convert milliseconds to hours, minutes, seconds, and milliseconds
     * @param {number} totalMs - Total milliseconds
     * @returns {Object} Time object with hours, mins, secs, ms properties
     */
    convertMillisecToTime = (totalMs) => {
        let totalSecs = Math.floor(totalMs / 1000);
        let hours = Math.floor(totalSecs / 3600);
        let mins = Math.floor((totalSecs % 3600) / 60);
        let secs = totalSecs % 60;
        let ms = totalMs % 1000;
        
        return { hours, mins, secs, ms };
    };

    /**
     * Start the countdown timer
     * Uses timestamp-based approach to prevent issues with browser throttling
     */
    start = () => {
        const { timerDuration } = this.props;
        
        if (!timerDuration) return;

        // Check if we have a saved target end time in localStorage
        if (localStorage.getItem("countdownTargetEndTime") !== null) {
            // Resume from existing target time
            this.targetEndTime = parseInt(localStorage.getItem("countdownTargetEndTime"));
        } else {
            // Calculate new target end time: current time + duration
            this.targetEndTime = Date.now() + timerDuration;
            localStorage.setItem("countdownTargetEndTime", this.targetEndTime);
            // Store initial duration for later calculation
            localStorage.setItem("initialTimerDuration", timerDuration);
        }

        if (!this.state.running) {
            this.setState({ running: true });
            this.watch = setInterval(() => this.countdown(), 100);
        }
    };

    /**
     * Stop the countdown timer
     */
    stop = () => {
        this.setState({ running: false });
        if (this.watch) {
            clearInterval(this.watch);
        }
    };

    /**
     * Pause the countdown timer without clearing the interval
     */
    pause = () => {
        if (this.targetEndTime && this.state.running) {
            // Calculate how much time was remaining when paused
            const now = Date.now();
            const remainingTime = this.targetEndTime - now;
            // Store the remaining time so we can resume from here
            localStorage.setItem("pausedRemainingTime", remainingTime.toString());
            // Clear the target end time to pause the countdown
            this.targetEndTime = null;
        }
    };

    /**
     * Resume the countdown timer from where it was paused
     */
    resume = () => {
        const pausedRemainingTime = localStorage.getItem("pausedRemainingTime");
        if (pausedRemainingTime && this.state.running) {
            // Set new target end time based on remaining time when paused
            this.targetEndTime = Date.now() + parseInt(pausedRemainingTime);
            // Clear the paused time from localStorage
            localStorage.removeItem("pausedRemainingTime");
        }
    };

    /**
     * Countdown logic - calculates remaining time and updates state
     * Triggers callbacks when timer reaches 00:00:00
     */
    countdown = () => {
        if (!this.state.running || !this.targetEndTime) {
            // If timer is paused (no targetEndTime), don't update countdown
            return;
        }

        // Calculate remaining time based on target end time
        const now = Date.now();
        const remainingTime = this.targetEndTime - now;

        // Check if time has reached 00:00:00 or passed
        if (remainingTime <= 0) {
            this.stop();
            this.setState({
                currentTimeHr: 0,
                currentTimeMin: 0,
                currentTimeSec: 0,
                currentTimeMs: 0,
            });
            
            // Clear localStorage
            localStorage.removeItem("countdownTargetEndTime");
            
            // Call the onTimerEnd callback from props (if provided)
            if (this.props.onTimerEnd) {
                this.props.onTimerEnd();
            }
            
            // Call the timerEndCallback from Redux state
            if (this.props.timerEndCallback) {
                this.props.timerEndCallback();
            }
            
            return;
        }

        // Convert remaining time to hours, minutes, seconds
        const timeObj = this.convertMillisecToTime(remainingTime);
        
        this.setState({
            currentTimeHr: timeObj.hours,
            currentTimeMin: timeObj.mins,
            currentTimeSec: timeObj.secs,
            currentTimeMs: timeObj.ms,
        });
    };

    /**
     * Reset the timer to initial state and clear localStorage
     */
    reset = () => {
        this.stop();
        this.setState({
            currentTimeHr: 0,
            currentTimeMs: 0,
            currentTimeSec: 0,
            currentTimeMin: 0,
        });
        localStorage.removeItem("countdownTargetEndTime");
        localStorage.removeItem("initialTimerDuration");
        localStorage.removeItem("pausedRemainingTime");
    };

    /**
     * Handle prop changes and trigger appropriate actions
     */
    componentDidUpdate(prevProps) {
        // Start timer when startTimer prop is true
        if (this.props.startTimer && this.props.startQCounter === 0) {
            startcount++;
            this.props.startQuizCounter();
            this.start();
        }
        
        // Stop timer when endTimer prop is true
        if (this.props.endTimer && this.props.endQCounter === 0) {
            endcount++;
            this.props.endQuizCounter();
            this.stop();
        }
        
        // Reset timer when isReset prop changes to true
        if (this.props.isReset && !prevProps.isReset) {
            this.reset();
        }
        
        // Handle pause/resume functionality
        if (this.props.isPaused !== prevProps.isPaused) {
            if (this.props.isPaused) {
                this.pause();
            } else {
                this.resume();
            }
        }
    }

    render() {
        // Calculate total remaining time in milliseconds
        const totalRemainingMs = 
            this.state.currentTimeHr * 3600000 +
            this.state.currentTimeMin * 60000 +
            this.state.currentTimeSec * 1000 +
            this.state.currentTimeMs;
        
        // Show red color when less than 5 minutes (300000ms) remaining
        const isLessThanFiveMinutes = totalRemainingMs < 300000;
        const isLessThanOneMinute = totalRemainingMs < 60000;

        return (
            <div className={`${classes.quizTimer} ${isLessThanOneMinute ? classes.pulseAnimation : ''}`}>
                <div className={classes.timerIconWrapper}>
                    <img 
                        src={timerIcon} 
                        alt="Quiz timer Icon" 
                        className={`${classes.timerIcon} ${isLessThanFiveMinutes ? classes.iconWarning : ''}`}
                    />
                </div>
                <div className={classes.timeDisplay}>
                    <div className={classes.timeSegments}>
                        <div className={classes.timeSegment}>
                            <span className={`${classes.timeValue} ${isLessThanFiveMinutes ? classes.redTimer : ''}`}>
                                {this.formatTime(this.state.currentTimeHr)}
                            </span>
                            <span className={classes.timeLabel}>hrs</span>
                        </div>
                        <span className={`${classes.timeSeparator} ${isLessThanFiveMinutes ? classes.redTimer : ''}`}>:</span>
                        <div className={classes.timeSegment}>
                            <span className={`${classes.timeValue} ${isLessThanFiveMinutes ? classes.redTimer : ''}`}>
                                {this.formatTime(this.state.currentTimeMin)}
                            </span>
                            <span className={classes.timeLabel}>min</span>
                        </div>
                        <span className={`${classes.timeSeparator} ${isLessThanFiveMinutes ? classes.redTimer : ''}`}>:</span>
                        <div className={classes.timeSegment}>
                            <span className={`${classes.timeValue} ${isLessThanFiveMinutes ? classes.redTimer : ''}`}>
                                {this.formatTime(this.state.currentTimeSec)}
                            </span>
                            <span className={classes.timeLabel}>sec</span>
                        </div>
                    </div>
                    {/* {isLessThanFiveMinutes && (
                        <div className={classes.warningMessage}>
                            {isLessThanOneMinute ? '⚠️ Less than 1 minute left!' : '⏰ Time is running out!'}
                        </div>
                    )} */}
                </div>
            </div>
        );
    }
}

/**
 * Map Redux state to component props
 * @param {Object} state - Redux store state
 * @returns {Object} Props derived from state
 */
const mapStateToProps = (state) => {
    return {
        startTimer: state.quizReducer.vQuizStarted,
        endTimer: state.quizReducer.vQuizEnded,
        startQCounter: state.quizReducer.startQCounter,
        endQCounter: state.quizReducer.endQCounter,
        timerEndCallback: state.quizReducer.timerEndCallback,
        isPaused: state.quizReducer.isTimerPaused,
    };
};

/**
 * Map dispatch actions to component props
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Object} Action creators bound to dispatch
 */
const mapDispatchToProps = (dispatch) => {
    return {
        startQuizCounter: () => dispatch(startQuizCounter()),
        endQuizCounter: () => dispatch(endQuizCounter()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(QuizCountdownTimer);
